import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MediaItem, Playlist } from "@gooner/shared";
import { RESERVED_COLLECTION_IDS } from "@gooner/shared";
import { apiErrorOf, userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { cx } from "../ui/primitives";
import { IconCheck, IconPlus } from "../ui/icons";
import { AuthModal } from "./AuthModal";
import { GoldDialog } from "./GoldDialog";
import { trackGoldGate } from "../../lib/gold";
import { MASK } from "../../lib/replayMask";

/** The menu's width (`w-44`) and roughly its tallest — list cap plus the New row — for
 *  placing it before it has rendered. */
const MENU_W = 176;
const MENU_MAX_H = 250;

/**
 * Small "add this clip to a collection" control. Opens a menu of the user's
 * collections (loaded on open), lets you add to any, and create a new one inline.
 * Anonymous users see the same button, but clicking it prompts sign-up.
 *
 * The menu is portalled: the button rides every ClipCard, and a card is `overflow-hidden`
 * — on a two-column phone grid a menu rendered in place, holding more than a few
 * collections, ran past the card's bottom edge and was cut off there.
 * It is still a React child of this button, so its clicks bubble to the card's slot
 * wrapper and stop there, and a pick can never read as a tap on the tile underneath.
 */
export function AddToCollection({ clip }: { clip: MediaItem }) {
  const user = useSession((s) => s.user);
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [goldGate, setGoldGate] = useState(false);
  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // An upload may go in the uploader's PRIVATE collections only (2026-10-28) — the server
  // refuses a public one, so the menu doesn't offer it.
  const isUpload = clip.sourceKind === "upload";
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  function toggleMenu() {
    if (open) {
      setOpen(false);
      return;
    }
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // Right edges aligned, kept on screen; opens upward when there is no room below.
    const left = Math.min(Math.max(8, r.right - MENU_W), window.innerWidth - MENU_W - 8);
    const below = r.bottom + 4 + MENU_MAX_H <= window.innerHeight || r.top < MENU_MAX_H;
    setPos(below ? { left, top: r.bottom + 4 } : { left, bottom: window.innerHeight - r.top + 4 });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    setError(null);
    // Nothing can be ADDED to either reserved collection from here. Your Uploads is the
    // vault's own membership, not a list. Faps is the crown's: adding through this menu
    // skipped the payout the crown pays, and the crown sits right beside this button.
    userApi
      .listPlaylists()
      .then((r) => setPlaylists(r.playlists.filter((p) => !RESERVED_COLLECTION_IDS.has(p.id) && !(isUpload && p.public))))
      .catch(() => setPlaylists([]));
    const inside = (t: EventTarget | null) =>
      !!(t instanceof Node && (ref.current?.contains(t) || menuRef.current?.contains(t)));
    const onDoc = (e: MouseEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    // Placed once, from where the button was — so a page that scrolls under it closes it
    // rather than leaving it floating over the wrong card. Its own list scrolling is fine.
    const onScroll = (e: Event) => {
      if (!inside(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, isUpload]);

  /** A refusal that isn't the Gold cap used to vanish here, and the click read as "nothing
   *  happens" (an upload's add, before 2026-10-28). Now it says what the server said. */
  function refused(e: unknown) {
    const err = apiErrorOf(e);
    // A full Basic collection (the per-collection ceiling, 2026-09-10) is a Gold
    // cap like the count of collections is — same dialog, same gate.
    if (err?.goldUpsell) {
      trackGoldGate("collections");
      setOpen(false);
      setGoldGate(true);
      return;
    }
    setError(err?.error ?? "Couldn't add that. Try again.");
  }

  async function add(pl: Playlist) {
    setError(null);
    try {
      await userApi.addToPlaylist(pl.id, clip);
      setAdded((s) => new Set(s).add(pl.id));
    } catch (e) {
      refused(e);
    }
  }

  async function createAndAdd() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      const pl = await userApi.createPlaylist(name, "add_menu");
      await userApi.addToPlaylist(pl.id, clip);
      setNewName("");
      setCreating(false);
      setPlaylists((prev) => [{ ...pl, clipCount: 1 }, ...(prev ?? [])]);
      setAdded((s) => new Set(s).add(pl.id));
    } catch (e) {
      // A Gold cap opens the upsell (the modal portals to body, so the popover's
      // outside-click unmount cannot take it down); other failures keep the form.
      refused(e);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Add to collection"
        title="Add to collection"
        onClick={(e) => {
          e.stopPropagation();
          if (!user) {
            setAuthOpen(true);
            return;
          }
          toggleMenu();
        }}
        className="flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-zinc-200 hover:bg-[var(--color-accent)]"
      >
        <IconPlus width={14} height={14} />
      </button>

      {authOpen && (
        <AuthModal
          initialMode="register"
          reason="Create a free account to save clips to collections."
          onClose={() => setAuthOpen(false)}
        />
      )}
      {goldGate && <GoldDialog source="collections" onClose={() => setGoldGate(false)} />}

      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={pos}
          // Above the clip popup (z-[60]), which this button also rides.
          className="fixed z-[70] w-44 overflow-hidden rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] py-1 text-left shadow-xl"
        >
          <div className="max-h-48 overflow-y-auto">
            {playlists === null ? (
              <p className="px-3 py-2 text-xs text-zinc-500">Loading…</p>
            ) : playlists.length === 0 ? (
              <p className="px-3 py-2 text-xs text-zinc-500">
                {isUpload ? "No private collections yet. Uploads can only go in private ones." : "No collections yet."}
              </p>
            ) : (
              playlists.map((pl) => (
                <button
                  key={pl.id}
                  type="button"
                  onClick={() => void add(pl)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-zinc-200 hover:bg-[var(--color-panel-2)]"
                >
                  {/* Your own collection names: masked in session replay (lib/replayMask). */}
                  <span className="truncate" {...MASK}>{pl.name}</span>
                  {added.has(pl.id) ? (
                    <IconCheck width={13} height={13} className="text-emerald-400" />
                  ) : null}
                </button>
              ))
            )}
          </div>

          {isUpload && playlists !== null && playlists.length > 0 ? (
            <p className="px-3 pb-1 text-[10px] leading-snug text-zinc-500">Uploads go in private collections only.</p>
          ) : null}
          {error ? (
            <p className="px-3 pb-1 text-[11px] leading-snug text-red-400" aria-live="polite">
              {error}
            </p>
          ) : null}

          <div className="border-t border-[var(--color-edge)] p-1.5">
            {creating ? (
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void createAndAdd();
                  if (e.key === "Escape") setCreating(false);
                }}
                onBlur={() => void createAndAdd()}
                placeholder="Collection name…"
                maxLength={80}
                className="w-full rounded bg-[var(--color-ink)] px-2 py-1 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className={cx(
                  "flex w-full items-center gap-1.5 rounded px-2 py-1 text-xs",
                  "text-[var(--color-accent-soft)] hover:bg-[var(--color-panel-2)]",
                )}
              >
                <IconPlus width={13} height={13} />
                New collection
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
